import { type HttpClient, query } from '../http';

import {
  type ActivityItem,
  type ListActivityQuery,
  type WeekSummary,
} from './activity.types';

/**
 * The household's shared timeline, and what it adds up to over a week.
 *
 * Warning: The two routes disagree on purpose about having no household.
 * `list` answers 400 — a feed of a family of one is nothing; `weekSummary`
 * answers about the caller alone, being a widget shown to everybody.
 *
 * @param http Transport supplied by the application.
 * @returns The `activity` domain of the API client.
 */
export function createActivityApi(http: HttpClient) {
  return {
    /**
     * Reads the household feed: finished games and wishlist additions merged
     * into one timeline, most recent first.
     *
     * A finished game only appears once it resolves to a household winner and
     * a runner-up to name — a solo game, or one no member scored in, is
     * skipped rather than told half.
     *
     * @param params How many entries to read.
     * @returns The entries, most recent first.
     * @throws `activity.error.list.not_in_family` (400) with no household.
     */
    list: (params: ListActivityQuery = {}) =>
      http.get<ActivityItem[]>(`/activity${query({ limit: params.limit })}`),

    /**
     * Counts the last seven days: games finished, games added, time played.
     *
     * @returns The counters, and the instant they run from.
     */
    weekSummary: () => http.get<WeekSummary>('/activity/week'),
  };
}

export type ActivityApi = ReturnType<typeof createActivityApi>;
